import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react'
import { tv, type VariantProps } from 'tailwind-variants';

const Icons = { X } as any;

interface DialogProps
    extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    Omit<VariantProps<typeof dialogStyles>, 'state'> {
    isOpen: boolean;
    children?: ReactNode;
    onClose: () => void;
    size?: 'small' | 'medium' | 'large' | 'xl' | 'full';
    title?: string;
    description?: string;
    showCloseButton?: boolean;
    preventCloseOnOverlayClick?: boolean;
    footer?: ReactNode;
    className?: string;
}

function cn(...args: Array<string | undefined | false | null>) {
    return args.filter(Boolean).join(' ');
}

const dialogStyles = tv({
    slots: {
        overlay:
            'fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6 ' +
            'bg-black/55 supports-[backdrop-filter]:backdrop-blur-md ' +
            'opacity-0 transition-opacity duration-[var(--duration-normal)] data-[state=open]:opacity-100 data-[state=closed]:opacity-0',

        panel:
            'relative grid grid-rows-[auto,1fr,auto] w-full max-h-[85vh] min-h-0 overflow-hidden rounded-[var(--radius-xl)] ' +
            'bg-[var(--bg-card)] ring-1 ring-white/4 shadow-[var(--shadow-modal)] ' +
            'translate-y-3 scale-[0.97] opacity-0 transition-[opacity,transform] duration-[var(--duration-normal)] ease-[var(--ease-calm)] ' +
            'data-[state=open]:translate-y-0 data-[state=open]:scale-100 data-[state=open]:opacity-100 ' +
            'data-[state=closed]:translate-y-3 data-[state=closed]:scale-[0.97] data-[state=closed]:opacity-0',

        header:
            'px-6 py-5 border-b border-[var(--border-subtle)] bg-[var(--bg-surface)]/25',

        title: 'text-lg sm:text-xl font-bold leading-tight truncate text-[var(--text-heading)] tracking-[-0.015em]',
        description: 'mt-1.5 text-[var(--font-secondary)] text-[var(--text-secondary)] leading-relaxed',

        body: 'px-6 py-6 overflow-y-auto custom-scrollbar bg-[var(--bg-card)]',

        footer:
            'px-6 py-4 border-t border-[var(--border-subtle)] bg-[var(--bg-surface)]/25',

        closeBtn:
            'absolute right-3.5 top-3.5 inline-flex h-9 w-9 items-center justify-center rounded-[var(--radius-md)] ' +
            'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] ' +
            'focus:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--primary-500)]/18 ' +
            'transition-all duration-[var(--duration-fast)] ' +
            'ring-offset-[var(--bg-card)]',
    },
    variants: {
        size: {
            small: { panel: 'max-w-md' },
            medium: { panel: 'max-w-lg' },
            large: { panel: 'max-w-2xl' },
            xl: { panel: '' },
            full: { panel: 'max-w-7xl w-[min(96vw,1280px)]' },
        },
        state: { open: {}, closed: {} },
    },
    defaultVariants: { size: 'medium', state: 'open' },
});

const Dialog = ({
    isOpen,
    children,
    onClose,
    size = 'medium',
    title,
    description,
    footer,
    showCloseButton = true,
    preventCloseOnOverlayClick = false,
    className = '',
    ...rest
}: DialogProps) => {
    const panelRef = useRef<HTMLDivElement | null>(null);
    const previouslyFocused = useRef<HTMLElement | null>(null);

    const [mounted, setMounted] = useState(isOpen);
    const [state, setState] = useState<'open' | 'closed'>(isOpen ? 'open' : 'closed');

    useEffect(() => {
        if (isOpen) {
            setMounted(true);
            requestAnimationFrame(() => setState('open'));
        } else if (mounted) {
            setState('closed');
            const t = setTimeout(() => setMounted(false), 180);
            return () => clearTimeout(t);
        }
    }, [isOpen, mounted]);

    const reactId = useMemo(() => Math.random().toString(36).slice(2, 10), []);
    const titleId = title ? `dialog-${reactId}-title` : undefined;
    const descId = description ? `dialog-${reactId}-desc` : undefined;

    useEffect(() => {
        if (!mounted) return;

        previouslyFocused.current = document.activeElement as HTMLElement | null;
        const { body } = document;
        const prevOverflow = body.style.overflow;
        body.style.overflow = 'hidden';

        const focusFirst = () => {
            const container = panelRef.current;
            if (!container) return;
            const focusables = container.querySelectorAll<HTMLElement>(
                'button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])'
            );
            (focusables[0] || container).focus();
        };
        const timer = setTimeout(focusFirst, 0);

        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
            } else if (e.key === 'Tab') {
                const container = panelRef.current;
                if (!container) return;
                const nodes = Array.from(
                    container.querySelectorAll<HTMLElement>(
                        'button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])'
                    )
                ).filter(n => !n.hasAttribute('disabled') && !n.getAttribute('aria-hidden'));
                if (!nodes.length) return;
                const first = nodes[0];
                const last = nodes[nodes.length - 1];
                const active = document.activeElement as HTMLElement;
                if (e.shiftKey && active === first) {
                    last.focus();
                    e.preventDefault();
                } else if (!e.shiftKey && active === last) {
                    first.focus();
                    e.preventDefault();
                }
            }
        };

        document.addEventListener('keydown', onKey, true);

        return () => {
            clearTimeout(timer);
            document.removeEventListener('keydown', onKey, true);
            body.style.overflow = prevOverflow;
            previouslyFocused.current?.focus?.();
        };
    }, [mounted, onClose]);

    if (!mounted) return null;

    const { overlay, panel, header, title: titleCls, description: descCls, body, footer: footerCls, closeBtn } =
        dialogStyles({ size, state });

    const handleOverlayClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
        if (e.target === e.currentTarget && !preventCloseOnOverlayClick) onClose();
    };

    return createPortal(
        <div className={overlay()} data-state={state} onClick={handleOverlayClick} role="presentation">
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={descId}
                className={cn(panel({ size, state }), className)}
                data-state={state}
                onClick={(e) => e.stopPropagation()}
                {...rest}
            >
                {(title || description || showCloseButton) && (
                    <div className={header()}>
                        <div className="flex items-start justify-between">
                            <div className="min-w-0 flex-1">
                                {title && <h3 id={titleId} className={titleCls()}>{title}</h3>}
                                {description && <p id={descId} className={descCls()}>{description}</p>}
                            </div>
                            {showCloseButton && (
                                <button type="button" onClick={onClose} className={closeBtn()} aria-label="Close dialog">
                                    <Icons.X className="h-5 w-5" />
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {children != null && children !== '' && !(Array.isArray(children) && children.length === 0) && (
                    <div className={body()} style={{ scrollbarGutter: 'stable' }}>
                        {children}
                    </div>
                )}

                {footer && <div className={footerCls()}>{footer}</div>}
            </div>
        </div>,
        document.body
    ) as unknown as React.ReactElement;
};

export default Dialog;
